import React from "react";
import * as Yup from "yup"; //https://www.npmjs.com/package/yap
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"; //フォームのバリデーションチェック

import { useLocation, useNavigate } from "react-router-dom";
//mui
import Box from "@mui/material/Box";
import Container from "@mui/material/Container/Container";
import InputAdornment from "@mui/material/InputAdornment/InputAdornment";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton/IconButton";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
//parts
import { FProvider, FCustomTextField } from "../../parts/HookForm";
// components
import { CustomButton } from "components/ui/buttons";

import { useSignin } from "api/auth";
import { AxiosResponse } from "axios";
import { useAuth, ISigninInfo, IAuthInfo } from "contexts/authContext";
import { Locale, useI18n } from "i18n";

import logoSrc from "assets/img/logo.png";

//初期値設定
const defaultValues = {
  username: "",
  password: "",
};

const Page = () => {
  //PrivateRouteからNavigate経由でlocationを受け取り、
  //アクセス先を制御する
  const location = useLocation();
  //アクセス先がない場合、トップ画面にリダイレクトする
  const { from } = location.state || { from: { pathname: "/" } };

  const [showPassword, setShowPassword] = React.useState(false);
  const navigate = useNavigate();
  const { signin } = useSignin();
  const { locale, setLocale, t } = useI18n();

  const { setCurrentUser } = useAuth();

  const [loginError, setLoginError] = React.useState<string | null>();

  //有効性検査
  const SigninSchema: Yup.ObjectSchema<ISigninInfo> = Yup.object({
    username: Yup.string().required(t("auth.requiredId")),
    password: Yup.string().required(t("auth.requiredPassword")),
  });

  // 2. useFormで必要な関数を取得し、デフォルト値を指定します。
  const methods = useForm<ISigninInfo>({
    resolver: yupResolver(SigninSchema),
    defaultValues,
  });

  const { handleSubmit } = methods;

  const onSubmit = (params: ISigninInfo) => {
    const { username, password } = params;
    if (setCurrentUser == null) return;

    setLoginError(null);
    signin(username, password)
      .then((res: AxiosResponse) => {
        const { data } = res;
        const authInfo: IAuthInfo = {
          user: data,
        };
        setCurrentUser(authInfo);
        navigate(from, { replace: true });
      })
      .catch((err) => {
        setLoginError(t("auth.invalidLogin"));
      });
  };

  //パスワード表示
  const onClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          position: "fixed",
          top: 16,
          right: 16,
          width: { xs: 180, sm: 240 },
          zIndex: 1,
        }}
      >
        <FormControl fullWidth size="small">
          <InputLabel id="signin-language-select-label">
            {t("app.language")}
          </InputLabel>
          <Select
            labelId="signin-language-select-label"
            value={locale}
            label={t("app.language")}
            onChange={(event) => setLocale(event.target.value as Locale)}
          >
            <MenuItem value="en-PH">{t("language.enPH")}</MenuItem>
            <MenuItem value="zh-TW">{t("language.zhTW")}</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ px: 2.5, py: 2 }}
          >
            <img src={logoSrc} alt="logo" width="80%" />
          </Box>
          <Box className="signinForm" sx={{ mt: 1 }}>
            <FProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <FCustomTextField
                name="username"
                label={t("auth.id")}
                margin="normal"
                autoFocus
              />
              <FCustomTextField
                name="password"
                label={t("auth.password")}
                margin="normal"
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={onClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {loginError && (
                <Box mt={2}>
                  <Alert severity="error">{loginError}</Alert>
                </Box>
              )}
              <CustomButton
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                {t("auth.signin")}
              </CustomButton>
            </FProvider>
          </Box>
        </Box>
      </Container>
    </React.Fragment>
  );
};

export default Page;
